import { TableCell } from "@mui/material";
import { useAnchorWallet } from "@solana/wallet-adapter-react";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { MESSAGE_TYPE } from "../../../../common/constants/common.constants";
import { CLUB_DETAILS } from "../../../../common/constants/routes.constants";
import { DAYS_PER_MONTH } from "../../../../common/constants/staking.constants";
import { StakingStatus } from "../../../../common/enums/staking.enum";
import { IDashboardStakingInfo } from "../../../../common/interfaces/user-dashboard.interface";
import {
  getMembersStakingInfoFromProgram,
  getStatusForStakingAccount,
  getUnqTokensAccountByMember,
} from "../../../../solana/common/helpers/staking.helpers";
import {
  claimTokens,
  getInfoForStakingAccount,
  stakeTokens,
} from "../../../../solana/methods/staking";
import { toggleModalOpenClass } from "../../../../utilities/helpers";
import { createNotification } from "../../../../utilities/notification";
import DepositStakingModal from "../../../ClubDetails/OverviewClub/Staking/StakingModal/DepositStakingModal/DepositStakingModal";
import ClubTableCell from "../../ClubTableCell/ClubTableCell";
import DashboardTableCell from "../../DashboardTable/DashboardTableCell/DashboardTableCell";
import DashboardTableRow from "../../DashboardTable/DashboardTableRow/DashboardTableRow";
import { rowCellStyle } from "../../DashboardTable/TableHeading/TableHeading.styles";

import "./StakingTableRow.scss";
import { dashboardStore } from "../../../../contexts/dashboardStore";
import { getUserRoleForClub } from "../../../../solana/methods/clubs";

const StakingTableRow: FC<{
  stakingInfo: IDashboardStakingInfo;
}> = ({ stakingInfo }) => {
  const [isDepositModalVisible, toggleDepositModal] = useState(false);
  const [isDepositing, toggleDepositing] = useState(false);
  const [userHasRole, toggleUserHasRole] = useState(false);
  const { stakings, setStakings } = dashboardStore();
  const wallet = useAnchorWallet();

  const getUserRole = useCallback(async () => {
    try {
      await getUserRoleForClub(wallet!, stakingInfo.club.clubName);
      toggleUserHasRole(true);
    } catch (error: any) {
      toggleUserHasRole(false);
    }
  }, [wallet, stakingInfo.club.clubName]);

  const claimStakedTokens = useCallback(
    async (e, claimNft: boolean) => {
      try {
        e.stopPropagation();
        const tokenAccount = await getUnqTokensAccountByMember(wallet!);
        const memberStakingInfo = await getMembersStakingInfoFromProgram(
          wallet!,
          stakingInfo.stakingAccountInfo.accountAddress
        );

        await claimTokens(
          stakingInfo.club.clubName,
          stakingInfo.stakingAccountInfo,
          tokenAccount?.tokenAccountAddress,
          wallet!,
          claimNft,
          memberStakingInfo
        );
        await updateStakingsFromContext();
        createNotification(MESSAGE_TYPE.SUCCESS, "Tokens successfully claimed");
      } catch (error: any) {
        createNotification(
          MESSAGE_TYPE.ERROR,
          "Claiming failed",
          error.message
        );
      }
    },
    [stakingInfo, wallet]
  );

  const updateStakingsFromContext = useCallback(async () => {
    const updatedStakingAccountInfo = await getInfoForStakingAccount(
      stakingInfo.stakingAccountInfo.accountAddress
    );
    const updatedMembersInfo = await getMembersStakingInfoFromProgram(
      wallet!,
      stakingInfo.stakingAccountInfo.accountAddress
    );
    const updatedStakingStatus = getStatusForStakingAccount(
      updatedMembersInfo,
      updatedStakingAccountInfo
    );
    const updatedStakings = [...stakings!];
    const stakingIndex = updatedStakings.findIndex(
      (item) =>
        item.stakingAccountInfo.accountAddress.toString() ===
        updatedStakingAccountInfo.accountAddress.toString()
    );

    updatedStakings[stakingIndex] = {
      club: stakingInfo.club,
      memberStakingInfo: updatedMembersInfo,
      stakingAccountInfo: updatedStakingAccountInfo,
      stakingStatus: updatedStakingStatus,
    };

    setStakings(updatedStakings);
  }, [stakingInfo, stakings, wallet]);

  const stakeUnqTokens = useCallback(
    async (tokenAccount, selectedAmount) => {
      try {
        toggleDepositing(true);
        await stakeTokens(
          stakingInfo.club.clubName,
          stakingInfo.stakingAccountInfo,
          wallet!,
          selectedAmount,
          tokenAccount
        );
        await updateStakingsFromContext();
        toggleDepositModal(false);
        toggleModalOpenClass(false);
        createNotification(MESSAGE_TYPE.SUCCESS, "Tokens staked successfully!");
      } catch (error: any) {
        createNotification(
          MESSAGE_TYPE.ERROR,
          "Problem staking tokens",
          error.message
        );
      } finally {
        toggleDepositing(false);
      }
    },
    [stakingInfo, wallet]
  );

  const stakeNft = useCallback(
    async (remainingAccounts) => {
      try {
        toggleDepositing(true);
        await stakeTokens(
          stakingInfo.club.clubName,
          stakingInfo.stakingAccountInfo,
          wallet!,
          1,
          undefined,
          remainingAccounts
        );

        await updateStakingsFromContext();
        toggleDepositModal(false);
        toggleModalOpenClass(false);
        createNotification(MESSAGE_TYPE.SUCCESS, "NFT staked successfully!");
      } catch (error: any) {
        createNotification(
          MESSAGE_TYPE.ERROR,
          "Problem staking NFT",
          error.message
        );
      } finally {
        toggleDepositing(false);
      }
    },
    [stakingInfo, wallet]
  );

  const shouldShowStakeButton = useMemo(() => {
    return (
      stakingInfo.stakingStatus === StakingStatus.FundingInitiated &&
      userHasRole
    );
  }, [stakingInfo.stakingStatus, userHasRole]);

  const shouldShowWithdrawButton = useMemo(() => {
    return (
      stakingInfo.stakingStatus === StakingStatus.FundingInitiated &&
      stakingInfo?.memberStakingInfo &&
      stakingInfo?.memberStakingInfo?.depositAmount > 0
    );
  }, [stakingInfo?.memberStakingInfo, stakingInfo.stakingStatus]);

  const shouldShowClaimButtons = useMemo(() => {
    return (
      (stakingInfo.stakingStatus === StakingStatus.StakingPeriodOver ||
        stakingInfo.stakingStatus === StakingStatus.StakingCanceled) &&
      stakingInfo.memberStakingInfo &&
      !stakingInfo.memberStakingInfo.hasClaimed
    );
  }, [stakingInfo]);

  const userClaimedTokens = useMemo(() => {
    if (
      wallet &&
      stakingInfo.stakingAccountInfo.nftStakeRecord &&
      stakingInfo.stakingAccountInfo.nftStakeRecord.nftOwner.toString() ===
        wallet.publicKey.toString()
    ) {
      if (
        stakingInfo.memberStakingInfo &&
        stakingInfo.memberStakingInfo.depositAmount > 0
      ) {
        return (
          stakingInfo.memberStakingInfo &&
          stakingInfo.memberStakingInfo.hasClaimed &&
          stakingInfo.stakingAccountInfo.nftStakeRecord.hasClaimed
        );
      } else return stakingInfo.stakingAccountInfo.nftStakeRecord.hasClaimed;
    }
    return (
      stakingInfo.memberStakingInfo && stakingInfo.memberStakingInfo.hasClaimed
    );
  }, [stakingInfo, stakingInfo, wallet]);

  const hasDepositedNft = useMemo(() => {
    return (
      !!stakingInfo.stakingAccountInfo.nftStakeRecord &&
      wallet &&
      stakingInfo.stakingAccountInfo.nftStakeRecord.nftOwner.toString() ===
        wallet.publicKey.toString()
    );
  }, [stakingInfo, wallet]);

  useEffect(() => {
    void getUserRole();
  }, []);

  return (
    <>
      {isDepositModalVisible && (
        <DepositStakingModal
          membersStakingInfo={stakingInfo.memberStakingInfo}
          onClose={() => {
            toggleDepositModal(false);
            toggleModalOpenClass(false);
          }}
          stakingAccount={stakingInfo.stakingAccountInfo}
          stakeUnqTokens={stakeUnqTokens}
          stakeNft={stakeNft}
          hasDepositedNft={hasDepositedNft}
          depositing={isDepositing}
        />
      )}
      <DashboardTableRow
        key={stakingInfo.club.clubAddress.toString()}
        redirectUrl={`${CLUB_DETAILS}/${stakingInfo.club.clubAddress}`}
      >
        <ClubTableCell
          clubImageUrl={stakingInfo.club.clubImageUrl}
          clubName={stakingInfo.club.clubName}
        />
        <DashboardTableCell value={stakingInfo.stakingAccountInfo.name} />
        <DashboardTableCell value={"UNQ Tokens"} />
        <DashboardTableCell
          value={
            stakingInfo.stakingAccountInfo.stakingPeriodType * DAYS_PER_MONTH
          }
        />
        <DashboardTableCell
          value={
            stakingInfo.memberStakingInfo
              ? stakingInfo.memberStakingInfo.depositAmount
              : 0
          }
        />
        <DashboardTableCell
          value={"6% APY "}
          span={
            stakingInfo.stakingAccountInfo.nftStakeRecord &&
            `+${stakingInfo.stakingAccountInfo.nftStakeRecord.additionalReward}%`
          }
        />
        <DashboardTableCell value={stakingInfo.stakingStatus} />
        {shouldShowStakeButton && (
          <TableCell style={rowCellStyle}>
            <button
              className="claim-button claim-button--stake"
              onClick={(e) => {
                e.stopPropagation();
                toggleDepositModal(true);
              }}
            >
              Stake
            </button>
          </TableCell>
        )}
        {shouldShowClaimButtons && (
          <TableCell style={rowCellStyle}>
            {userClaimedTokens ? (
              <button className="claim-button" disabled={true}>
                Claimed
              </button>
            ) : (
              <button
                className="claim-button claim-button--claim"
                onClick={(e) => claimStakedTokens(e, false)}
              >
                Claim
              </button>
            )}
          </TableCell>
        )}
        {shouldShowWithdrawButton && (
          <TableCell style={rowCellStyle}>
            <button
              className="claim-button claim-button--stake"
              onClick={(e) => claimStakedTokens(e, false)}
            >
              Withdraw
            </button>
          </TableCell>
        )}
        {stakingInfo.stakingStatus === StakingStatus.FundingInitiated &&
          hasDepositedNft && (
            <TableCell style={rowCellStyle}>
              <button
                className="claim-button claim-button--stake"
                onClick={(e) => claimStakedTokens(e, true)}
              >
                Withdraw NFT
              </button>
            </TableCell>
          )}
      </DashboardTableRow>
    </>
  );
};

export default StakingTableRow;
